import { getType, types } from 'mobx-state-tree';
import Dependency, { type IDependency } from './dependency';
import Inheritance, { type IInheritance } from './inheritance';
import { modelReference } from '../refHelper';
import * as utils from './mpc_utils';
import type { IObjectType } from './object_type';

// A path element is a reference to either a dependency or an inheritance.
type PathElement = IDependency | IInheritance;

/**
 * The Path instance interface.
 *
 * HAND-DECLARED so its `elements` reference array reads as the concrete
 * `PathElement` union and so sibling views see each other. Hand-declaring also
 * keeps MultiplePropagationConstraint (which holds `IPath[]`) from pulling
 * `typeof Path` into the recursion.
 */
export interface IPath {
  readonly id: string;
  readonly name: string;
  readonly primary: boolean;
  readonly elements: PathElement[];
  readonly fingerprint: string;
  readonly firstObject: IObjectType | undefined;
  readonly lastObject: IObjectType | undefined;
  readonly shortName: string;
  readonly displayName: string;
  readonly accessorChain: IDependency[];
  readonly information: string;
  includes(element: PathElement): boolean;
  isPartOf(elements: readonly PathElement[] | null | undefined): boolean;
  startsWith(elements: readonly PathElement[] | null | undefined): boolean;
  endsWith(elements: readonly PathElement[] | null | undefined): boolean;
  isTailOf(elements: readonly PathElement[] | null | undefined): boolean;
  isHeadOf(elements: readonly PathElement[] | null | undefined): boolean;
  equals(other: { fingerprint: string }): boolean;
  overlappingTail(elements: readonly PathElement[]): PathElement[];
  overlappingDependencyTail(elements: readonly PathElement[]): PathElement[];
}

/**
 * A path in a MERODE multiple propagation constraint
 */
const Path = types
  .model('Path', {
    id: types.identifier,
    name: types.optional(types.string, ''),
    primary: true,
    // The element reference resolves to a dependency or an inheritance; the type
    // argument re-establishes that concrete union (see refHelper.modelReference).
    elements: types.array(
      modelReference(
        types.union(Dependency, Inheritance)
      )
    ),
  })
  .views((self0) => {
    const self = self0 as unknown as IPath;
    return {
    // get a fingerprint that can be used to check if two paths are actually the
    // same sequence of elements
    get fingerprint() {
      return utils.fingerprint(self.elements);
    },
    // get the master/supertype of the first element in the path
    get firstObject() {
      if (self.elements.length > 0) {
        return self.elements[0].previousObject;
      }
    },
    // get the dependent/subtype of the last element in the path
    get lastObject() {
      if (self.elements.length > 0) {
        return self.elements[self.elements.length - 1].nextObject;
      }
    },
    // get the name or a short discription if the name is empty
    get shortName() {
      if (self.name != null && self.name !== '') {
        return self.name;
      } else {
        return `${self.firstObject!.name} --> ${self.lastObject!.name}`;
      }
    },
    // Used a.o. in the inspector
    get displayName() {
      if (self.elements.length > 0) {
        return `${self.firstObject!.name}${self.elements
          .map((e) => `--[${e.displayName}]-->${e.nextObject.name}`)
          .join('')}`;
      } else {
        return '';
      }
    },
    // Only use dependencies for this as inheritance relations are is-a relations and thus define no
    // accessor.
    // Reverse the list since that is how the accessors are traversed.
    // The filter keeps only Dependency elements, so the result is IDependency[].
    get accessorChain(): IDependency[] {
      return self.elements.filter((e) => getType(e) === Dependency).reverse() as IDependency[];
    },
    // Get a short description in the form or an accessor chain
    get information() {
      return self.accessorChain.map((d) => d.name.master).join('.');
    },
    // does the path include the given element
    includes(element: PathElement) {
      return self.elements.includes(element);
    },
    // is the path part of the path with the given elements
    isPartOf(elements: readonly PathElement[] | null | undefined) {
      if (elements == null || elements.length === 0 || self.elements.length === 0) {
        return false;
      }
      const theirFingerprint = utils.fingerprint(elements);
      return theirFingerprint.includes(self.fingerprint);
    },
    // does the path start with the path with the given elements
    startsWith(elements: readonly PathElement[] | null | undefined) {
      return utils.pathStartsWith(self.elements, elements);
    },
    // does the path end with the path with the given elements
    endsWith(elements: readonly PathElement[] | null | undefined) {
      return utils.pathEndsWith(self.elements, elements);
    },
    // is the path the tail of the path with the given elements
    isTailOf(elements: readonly PathElement[] | null | undefined) {
      return utils.pathIsTailOf(self.elements, elements);
    },
    // is the path the head of the path with the given elements
    isHeadOf(elements: readonly PathElement[] | null | undefined) {
      return utils.pathIsHeadOf(self.elements, elements);
    },
    // is this the same logical path as the other?
    equals(other: { fingerprint: string }) {
      return self.fingerprint === other.fingerprint;
    },
    // get the overlapping tail between this path and the given elements
    overlappingTail(elements: readonly PathElement[]) {
      return utils.overlappingTail(self.elements, elements);
    },
    // get the overlapping dependency tail between this path and the given elements
    overlappingDependencyTail(elements: readonly PathElement[]) {
      const commonTail = self.overlappingTail(elements);
      const reversedCommonTail = commonTail.reverse();
      const result: PathElement[] = [];
      for (const element of reversedCommonTail) {
        const type = getType(element);
        if (type === Inheritance) {
          break;
        }
        result.push(element as PathElement);
      }
      return result.reverse();
    },
    };
  })
  .actions((self) => ({}));

export default Path;
